// ── Fee heads ────────────────────────────────────────────────────────────────
//
// What a charge is FOR. The canonical vocabulary, shared so the Record Payment
// picker, the API's Zod schema and the audit script all offer and accept the
// same five values. The API keeps its own copy in `apps/api/src/lib/fee-heads.ts`
// (it cannot import runtime values from this package — raw `.ts`), and
// `fee-heads.test.ts` there asserts parity against this list, the same
// arrangement `academic-year.ts` uses.
//
// OTHER is the residual bucket a human picks deliberately. It is NOT the
// fallback for "nobody said": that is `null` on FeePayment.feeHead, and it
// means a plan installment covering several heads at once. Screens render "—"
// and say why rather than filing an undescribed charge under a head.
//
// Extracted into its own leaf module (2026-09-28, Record Payment "Apply
// towards" rework) so `apply-to.ts` and `head-breakdown.ts` can import the
// vocabulary without a circular import back through this package's
// `types/index.ts` barrel (which re-exports this file).

//
// DEPOSIT (2026-10-02, WP-12) is a refundable SECURITY DEPOSIT, not a fee: the
// school holds it for the family, so the ledger books it to the liability
// account `SECURITY_DEPOSITS` and never to income (apps/api
// `lib/ledger/account-map.ts` `feeIncomeKey`). It is last on purpose: the order
// decides which head absorbs a rounding remainder.
export const FEE_HEADS = ["TUITION", "TRANSPORT", "HOSTEL", "ADMISSION", "OTHER", "DEPOSIT"] as const;
export type FeeHead = (typeof FEE_HEADS)[number];

export const FEE_HEAD_LABELS: Record<FeeHead, string> = {
  TUITION:   "Tuition",
  TRANSPORT: "Transport",
  HOSTEL:    "Hostel / boarding",
  ADMISSION: "Admission",
  OTHER:     "Other",
  DEPOSIT:   "Security deposit",
};

/** One line of plain English per head, for the picker's helper text. */
export const FEE_HEAD_HINTS: Record<FeeHead, string> = {
  TUITION:   "Academic fees — term, exam, lab, books",
  TRANSPORT: "Bus or van — the seat, not the class fee",
  HOSTEL:    "Boarding, room, mess",
  ADMISSION: "Admission, registration, re-admission",
  OTHER:     "Anything else — say what in the notes",
  DEPOSIT:   "Refundable deposit the school holds for the family — never income",
};

export function isFeeHead(value: unknown): value is FeeHead {
  return typeof value === "string" && (FEE_HEADS as readonly string[]).includes(value);
}
